import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import CustomForm, { makePersistValue } from "../common/components/CustomForm";
import DefaultButton from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import useMessageDisplay from "../common/components/MessageDisplay";
import ReactForm from "../common/components/ReactForm";
import useNetwork from "../common/hooks/networkHook";
import { deepClone, deepMerge } from "../common/utils/objectUtilities";
import { submitPublicForm, usePublicForm } from "../reducers/publicFormSlice";
import { usePublicNavBar } from "./NavBar";
import { useLocalState } from "../common/hooks/localStorageHook";

const PublicFormImpl = ({ hideNav: hideNavInput, code }) => {
    const { t } = useTranslation();
    const navBar = usePublicNavBar();
    const hideNav = hideNavInput ?? navBar?.hideNav
    const network = useNetwork();
    const { showError } = useMessageDisplay();
    
    const [searchParams] = useSearchParams();
    const { jobid, ...params } = Object.fromEntries(searchParams.entries());
    const [savedData, setSavedData] = useLocalState('publicForm_' + code, persisted => ({ ...persisted, ...params }));
    const [data, setData] = useState(savedData);
    const [state, setState] = useState('edit');
    const [result, setResult] = useState()
    const form = usePublicForm(code);
    const formRef = useRef();

    const formDef = useMemo(() => {
        let formDef = form?.def?.map(section => ({ ...section, rows: section.rows?.map(row => row.filter(cell => !cell.restricted))})) ?? [];
        formDef = formDef.filter(section => {
            if (!section.rows) {
                return section;
            }
            section.rows = section.rows.filter(row => row.length > 0);
            return section.rows.length > 0;
        });
        formDef.forEach(section => section.borderless = false);
        return formDef;
    }, [form?.def]);

    useEffect(() => navBar?.setTitle(form?.title), [navBar, form?.title]);

    const handleUpdate = useCallback(async delta => {
        setData(data => {
            const dataPrime = deepMerge(deepClone(data), delta);
            makePersistValue({ def: formDef, data: dataPrime }).then(persist => setSavedData(persist));
            return dataPrime;
        });
    }, [formDef, setSavedData]);

    const submit = async () => {
        setState('submitting')
        if (formRef.current.checkValidity()) {
            const currentData = await new Promise(resolve =>
                setData(data => { resolve(data); return data; })
            );
            const result = await submitPublicForm(network, code, { v2: true, ...currentData }, jobid);
            setResult({ ...result, submit_message: result.submit_message ?? t('publicFormSubmitSuccess', result) });
            setState('submitted');
        } else {
            showError(t('publicFormSubmitError'));
            setState('edit');
        }
    }

    let body = <ReactForm ref={formRef}>
        <CustomForm def={formDef} data={data} onChange={handleUpdate} required={true}></CustomForm>
    </ReactForm>
    if (state === 'submitted' && result) {
        if (typeof result.submit_message === 'string') {
            body = <div style={{}} dangerouslySetInnerHTML={{ __html: result.submit_message }}></div>
        } else {
            body = <CustomForm data={result} def={result.submit_message} readonly></CustomForm>
        }
    }

    return form && <div style={{ backgroundColor: '#186ADB', height: '100%', ...form.style }}>
        {
            !hideNav && state !== 'submitted' && navBar?.placeControls(<Col xs="auto">
                <Row>
                    <Col xs="auto" className="mx-2 py-3">
                        <DefaultButton kind="clear" confirmMessage={t('publicFormClear')} onClick={() => { setData({}); setSavedData({}) }}></DefaultButton>
                    </Col>
                    <Col xs="auto" className="py-3">
                        <DefaultButton kind="submit" disabled={state !== 'edit'} confirmMessage={t('publicFormSubmit')} onClick={submit}></DefaultButton>
                    </Col>
                </Row>
            </Col>)
        }
        <Row className="py-1 px-3">
            <Col>
                { body }
            </Col>
        </Row>
        { hideNav && state !== 'submitted' && <Row className="py-1 px-3">
            <Col xs></Col>
            <Col xs="auto" className="mx-2 py-3">
                <DefaultButton kind="clear" confirmMessage={t('publicFormClear')} disabled={state === 'submitting'} onClick={() => setData({})}></DefaultButton>
            </Col>
            <Col xs="auto" className="py-3">
                <DefaultButton kind="submit" disabled={state !== 'edit'} processing={state === 'submitting'} confirmMessage={t('publicFormSubmit')} onClick={submit}></DefaultButton>
            </Col>
        </Row>}
    </div> 
}

export const PublicForm = () => {
    const { code } = useParams();
    
    return <PublicFormImpl code={code}></PublicFormImpl>
}

export const AngularPublicForm = ({ code }) => {
    return <PublicFormImpl hideNav={true} code={code}></PublicFormImpl>
}